import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from 'firebase/auth';
import { cn } from '@talk2nick/ui';

import type { AuthState } from './useAuth';
import { purgeLockedCache, useLockStore, useLockWatcher } from './useLockStore';
import { PinResetScreen } from './PinResetScreen';
import { clearPinResetRequest, isPinResetPending } from './pinReset';
import { hasRegisteredBiometrics, verifyBiometric } from './biometrics';
import { triggerHaptic } from '../ui/haptics';

/**
 * PIN 잠금 게이트 (PLAN 5-12장).
 *
 * 잠금은 Firebase 세션과 별개인 이 기기의 게이트다. 앱을 다시 열거나 5분 넘게
 * 백그라운드에 있다 돌아오면, 인증 세션이 살아 있어도 여기서 라우트를 렌더하지
 * 않는다 → 잠금 중에는 Firestore 구독도 올라오지 않는다.
 */
export function LockGate({ auth, children }: { auth: AuthState; children: ReactNode }) {
  const { isLocked, remainingAttempts, tryUnlock, clearLock } = useLockStore(auth.firebaseUser);
  // PIN을 아직 만들지 않은 사용자(온보딩 중)는 잠글 대상이 아니다
  const pinConfigured = auth.profile?.settings?.pin !== undefined;

  useLockWatcher(pinConfigured, isLocked);

  // 잠금으로 들어가는 순간 로컬 흔적을 지운다 (PLAN 5-12장 — 구독 끊기 + IndexedDB 캐시).
  // purgeLockedCache는 재진입해도 안전해서 StrictMode 이중 호출도 문제 없다.
  useEffect(() => {
    if (isLocked) void purgeLockedCache();
  }, [isLocked]);

  // 잠금 상태 분기 처리:
  // 1. 미인증: 로그인 화면으로 즉시 통과
  // 2. 미잠금: 즉시 통과
  // 3. 잠금 중 세션/프로필 로딩: 로딩 화면 표시
  // 4. 프로필 로드 후 PIN 미설정: 잠금 없이 통과
  // 5. PIN 설정 완료: LockScreen 표시
  if (auth.status === 'unauthenticated') {
    return <>{children}</>;
  }

  // 잠금 상태가 아니면 즉시 통과
  if (!isLocked) {
    return <>{children}</>;
  }

  // 잠금 상태일 때: 인증 복원 또는 프로필 스냅샷 대기 중이면 로딩 표시
  if (auth.status === 'loading' || !auth.profileLoaded) {
    return (
      <div className="app-shell items-center justify-center bg-[var(--bg-app)]">
        <LoadingText />
      </div>
    );
  }

  // 프로필 로드 완료 후, PIN이 미설정된 계정(온보딩 단계 등)이면 잠금 통과
  if (!pinConfigured) {
    return <>{children}</>;
  }

  return (
    <LockScreen
      user={auth.firebaseUser}
      remainingAttempts={remainingAttempts}
      tryUnlock={tryUnlock}
      onPinReset={clearLock}
    />
  );
}

function LoadingText() {
  const { t } = useTranslation();
  return <p className="text-sm text-[var(--text-secondary)]">{t('status.loading')}</p>;
}

function LockScreen({
  user,
  remainingAttempts,
  tryUnlock,
  onPinReset,
}: {
  user: User | null;
  remainingAttempts: number;
  tryUnlock: (pin: string) => Promise<void>;
  onPinReset: () => void;
}) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);

  // 모바일 리디렉션에서 돌아온 경우에는 곧바로 재설정 화면을 연다
  const [resetting, setResetting] = useState(isPinResetPending);
  const hasBio = user ? hasRegisteredBiometrics(user.uid) : false;

  async function onBiometricUnlock() {
    if (!user || verifying) return;
    setError(null);
    setVerifying(true);
    triggerHaptic('medium');
    try {
      const ok = await verifyBiometric(user.uid);
      if (ok) {
        triggerHaptic('success');
        onPinReset();
      }
    } catch (err) {
      console.warn('[talk2nIcK] 생체인식 해제 실패:', err);
      triggerHaptic('warning');
    } finally {
      setVerifying(false);
    }
  }

  useEffect(() => {
    if (hasBio && !resetting) {
      void onBiometricUnlock();
    }
  }, [hasBio, resetting]);

  // 잠금 화면은 소프트 키보드를 띄우지 않는다. 이전 화면에서 따라온 포커스든
  // 브라우저가 되살린 포커스든 입력 요소에 닿는 즉시 blur 해서 키보드를 닫고,
  // 이후에도 입력 요소가 포커스를 잡지 못하도록 막는다. 번호 입력은 아래 숫자
  // 키패드와 window 훅의 물리 키보드가 받는다. PIN 재설정 화면은 텍스트 입력이
  // 필요하므로 그때만 이 가드를 끈다.
  useEffect(() => {
    if (resetting) return;
    function blurEditable(target: EventTarget | null) {
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        target.blur();
      }
    }
    blurEditable(document.activeElement);
    const onFocusIn = (e: FocusEvent) => blurEditable(e.target);
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [resetting]);

  // 데스크톱 / 물리 키보드 번호키 및 백스페이스 지원 (소프트 키보드는 띄우지 않음)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (verifying || resetting) return;
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [verifying, resetting, pin]);

  async function submitPin(pinToSubmit: string) {
    if (verifying || pinToSubmit.length !== 6) return;
    setError(null);
    setVerifying(true);
    try {
      await tryUnlock(pinToSubmit);
      triggerHaptic('success');
    } catch (err) {
      triggerHaptic('warning');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 450);

      const message = (err as { message?: string }).message;
      setError(
        message === 'status.5attempts'
          ? t('status.attemptsExceeded')
          : message === 'status.wrongPin'
            ? t('status.wrongPin')
            : message === 'status.noProfile'
              ? t('status.noProfile')
              : message === 'status.noAuth'
                ? t('auth.googleSignIn')
                : t('error.generic'),
      );
    } finally {
      setPin('');
      setVerifying(false);
    }
  }

  function handleDigit(digit: string) {
    if (verifying || pin.length >= 6) return;
    triggerHaptic('light');
    const next = (pin + digit).slice(0, 6);
    setPin(next);
    if (next.length === 6) {
      void submitPin(next);
    }
  }

  function handleBackspace() {
    if (verifying || pin.length === 0) return;
    triggerHaptic('selection');
    setPin((prev) => prev.slice(0, -1));
  }

  if (resetting && user) {
    return (
      <PinResetScreen
        user={user}
        onReset={onPinReset}
        onCancel={() => {
          clearPinResetRequest();
          setResetting(false);
        }}
      />
    );
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col items-center justify-between gap-4 overflow-y-auto max-h-[calc(var(--app-height,100dvh)_-_var(--kb-height,0px))] px-6 pt-[max(var(--safe-top),2rem)] pb-[max(var(--safe-bottom),2rem)] text-center select-none bg-[radial-gradient(ellipse_at_top,_var(--accent-glow),_transparent_65%)]">
      {/* 헤더 및 타이틀 */}
      <div className="flex flex-col items-center gap-3 w-full max-w-xs">
        <div className="relative flex items-center justify-center">
          <div className="absolute -inset-3 rounded-full bg-[var(--accent)]/15 blur-lg pointer-events-none" />
          <div className="text-5xl relative drop-shadow-sm">🔒</div>
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
          {t('status.locked')}
        </h1>
        <p className="text-xs text-[var(--text-secondary)]">{t('status.lockedHint')}</p>

        {/* 6개 키네틱 버블 인디케이터 (PLAN UI_UX.md Module 1) */}
        <div
          className={cn(
            'flex items-center justify-center gap-4 py-4',
            isShaking && 'animate-shake',
          )}
        >
          {[0, 1, 2, 3, 4, 5].map((idx) => {
            const filled = pin.length > idx;
            return (
              <span
                key={idx}
                className={cn(
                  'h-3.5 w-3.5 rounded-full border-2 transition-all duration-200',
                  filled
                    ? 'scale-125 border-[var(--accent)] bg-[var(--accent)] shadow-[0_0_10px_var(--accent)] animate-pop-spring'
                    : isShaking
                      ? 'border-[var(--danger)] bg-transparent'
                      : 'border-[var(--divider)] bg-[var(--bg-app)]',
                )}
              />
            );
          })}
        </div>

        {error && <p className="text-xs font-medium text-[var(--danger)]">{error}</p>}
        {remainingAttempts > 0 && !error && (
          <p className="text-xs text-[var(--text-secondary)]">
            {remainingAttempts === 1
              ? t('status.1attemptLeft')
              : t('status.remainingAttempts', { count: remainingAttempts })}
          </p>
        )}
      </div>

      {/* 모바일 텍타일 숫자 키패드 (1-9, 생체인식/0/백스페이스) */}
      <div className="w-full max-w-[300px]">
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => handleDigit(String(num))}
              disabled={verifying}
              className="pressable flex h-14 w-14 mx-auto items-center justify-center rounded-full bg-[var(--bg-surface-elevated)] border border-[var(--divider)] text-xl font-semibold text-[var(--text-primary)] shadow-sm hover:border-[var(--accent)]/50 active:scale-90 transition-all disabled:opacity-50"
            >
              {num}
            </button>
          ))}

          {/* 하단 행: 생체인식 / 0 / 백스페이스 */}
          <div className="flex h-14 w-14 mx-auto items-center justify-center relative">
            {hasBio && (
              <div className="absolute inset-0 rounded-full border border-[var(--accent)] animate-pulse-ring pointer-events-none" />
            )}
            <button
              type="button"
              onClick={() => void onBiometricUnlock()}
              disabled={!hasBio || verifying}
              aria-label="생체인식 잠금 해제"
              className={cn(
                'pressable flex h-14 w-14 items-center justify-center rounded-full text-xl transition-all',
                hasBio
                  ? 'bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/40 hover:bg-[var(--accent)]/25 active:scale-90'
                  : 'opacity-0 pointer-events-none',
              )}
            >
              🧬
            </button>
          </div>

          <button
            type="button"
            onClick={() => handleDigit('0')}
            disabled={verifying}
            className="pressable flex h-14 w-14 mx-auto items-center justify-center rounded-full bg-[var(--bg-surface-elevated)] border border-[var(--divider)] text-xl font-semibold text-[var(--text-primary)] shadow-sm hover:border-[var(--accent)]/50 active:scale-90 transition-all disabled:opacity-50"
          >
            0
          </button>

          <button
            type="button"
            onClick={handleBackspace}
            disabled={verifying || pin.length === 0}
            aria-label="지우기"
            className="pressable flex h-14 w-14 mx-auto items-center justify-center rounded-full text-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] active:scale-90 transition-all disabled:opacity-30"
          >
            ⌫
          </button>
        </div>
      </div>

      {/* 하단 보조 액션 (PIN 잊음) */}
      <div className="w-full max-w-xs space-y-2">
        {user && (
          <button
            type="button"
            className="tap-target w-full text-xs text-[var(--text-secondary)] hover:text-[var(--accent)] underline transition-colors"
            onClick={() => setResetting(true)}
            disabled={verifying}
          >
            {t('status.forgotPin')}
          </button>
        )}
      </div>
    </div>
  );
}
